import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Github, Linkedin, Mail, Phone, MapPin, Download, ExternalLink, ArrowRight, X as XIcon } from 'lucide-react';

const Portfolio = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [openerActive, setOpenerActive] = useState(false);
  const [openerTitle, setOpenerTitle] = useState('');
  const [robotPos, setRobotPos] = useState('bottom-right');
  const [selectedProject, setSelectedProject] = useState(null);
  const openerOverlayRef = useRef(null);
  const robotRef = useRef(null);
  const robotYRef = useRef(0);
  const robotTargetYRef = useRef(0);
  const rafRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      const sections = ['home', 'about', 'projects', 'contact'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const onLoad = () => {
      document.documentElement.classList.add('preloader-active');

      setOpenerTitle('Hello Me');
      setOpenerActive(true);
      if (openerOverlayRef.current) {
        openerOverlayRef.current.classList.remove('hide-overlay');
        openerOverlayRef.current.classList.add('show-overlay');
      }

      setTimeout(() => {
        if (openerOverlayRef.current) openerOverlayRef.current.classList.add('hide-overlay');
      }, 1000);

      setTimeout(() => {
        document.documentElement.classList.remove('preloader-active');
        setOpenerActive(false);
        setIsLoading(false);
        setTimeout(() => setRobotPos('hero-top-left'), 300);
      }, 2000);
    };

    if (document.readyState === 'complete') onLoad();
    else window.addEventListener('load', onLoad);

    return () => {
      window.removeEventListener('load', onLoad);
      document.documentElement.classList.remove('preloader-active');
    };
  }, []);

  // ===== REMOVED: Section-wise pop up animation =====
  // এখন শুধু page load এর সময় animation থাকবে

  useEffect(() => {
    const robot = robotRef.current;
    if (!robot) return;

    const onScroll = () => {
      const y = window.scrollY;
      robotTargetYRef.current = Math.min(120, y * 0.04);
    };

    const loop = () => {
      const current = robotYRef.current;
      const target = robotTargetYRef.current;
      const next = current + (target - current) * 0.12;
      robotYRef.current = next;
      robot.style.transform = `translateY(${next}px)`;
      rafRef.current = requestAnimationFrame(loop);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    rafRef.current = requestAnimationFrame(loop);

    let popupTimer = null;
    const schedule = () => {
      const delay = 8000 + Math.random() * 6000;
      popupTimer = setTimeout(() => {
        robot.classList.add('robot-pop');
        setTimeout(() => robot.classList.remove('robot-pop'), 2200);
        schedule();
      }, delay);
    };
    schedule();

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (popupTimer) clearTimeout(popupTimer);
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('Thank you for your message! I will get back to you soon.');
    setFormData({ name: '', email: '', message: '' });
  };

  const projects = [
    {
      title: 'ERP System',
      category: 'Laravel',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop',
      link: '#',
      desc: 'Enterprise Resource Planning system with comprehensive modules',
      details: 'Full-featured ERP system designed for enterprise resource management with modules for inventory, sales, and reporting. Built with Laravel and MySQL for maximum scalability and performance.'
    },
    {
      title: 'Event Management',
      category: 'Laravel',
      image: 'https://images.unsplash.com/photo-1511578314322-379afb476865?w=400&h=300&fit=crop',
      link: 'https://bridalharmony.com.bd/',
      desc: 'Full-featured event booking and management platform',
      details: 'Comprehensive event management platform with booking system, payment integration, and customer management. Handles multiple events and thousands of bookings.'
    },
    {
      title: 'E-Commerce Platform',
      category: 'Laravel',
      image: 'https://images.unsplash.com/photo-1557821552-17105176677c?w=400&h=300&fit=crop',
      link: 'https://github.com/Leaya0214/Ecommerce-website',
      desc: 'Scalable e-commerce solution with payment integration',
      details: 'Complete e-commerce solution featuring product management, cart system, payment gateway integration, and order tracking. Built for high traffic and scalability.'
    },
    {
      title: 'HRM System',
      category: 'CodeIgniter',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=300&fit=crop',
      link: '#',
      desc: 'Human Resource Management with payroll automation',
      details: 'Advanced HRM system with employee management, attendance tracking, leave management, and automated payroll processing.'
    },
    {
      title: 'POS Software',
      category: 'CodeIgniter',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop',
      link: '#',
      desc: 'Point of Sale system for retail operations',
      details: 'Robust POS system for retail businesses with inventory management, sales reporting, and multi-user support.'
    },
    {
      title: 'BRRI VMS',
      category: 'Laravel',
      image: 'https://images.unsplash.com/photo-1551434678-e076c223a692?w=400&h=300&fit=crop',
      link: '#',
      desc: 'Visitor Management System for institutions',
      details: 'Smart visitor management system with check-in/check-out, badge printing, and security logging for institutions.'
    },
    {
      title: 'TeamTaskPro',
      category: 'Laravel',
      image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop',
      link: 'https://github.com/Leaya0214/TeamTaskPro',
      desc: 'Project and task management platform for teams',
      details: 'Collaborative project management tool with task assignment, progress tracking, team collaboration features, and real-time updates.'
    },
    {
      title: 'Library Management',
      category: 'PHP/MySQL',
      image: 'https://images.unsplash.com/photo-1507842845185-6c05246c0b5f?w=400&h=300&fit=crop',
      link: 'https://github.com/Leaya0214/Library-Management',
      desc: 'Complete library management system with inventory control',
      details: 'Full-featured library management system handling book cataloging, member management, issue/return tracking, and fine management.'
    }
  ];

  const splitTitle = (title) => {
    if (!title) return { left: '', right: '' };
    const parts = title.split(' ');
    if (parts.length > 1) {
      return { left: parts.slice(0, parts.length - 1).join(' '), right: parts.slice(-1).join(' ') };
    }
    const mid = Math.ceil(title.length / 2);
    return { left: title.slice(0, mid), right: title.slice(mid) };
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-black/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}`}>
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex justify-between items-center h-20">
            <button onClick={() => scrollToSection('home')} className="text-2xl font-bold text-white tracking-tight hover:text-gray-300 transition-colors duration-300 hover:scale-105 transform">
              leaya<span className="text-cyan-400">.</span>
            </button>

            <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2">
              <div className="flex space-x-12">
                {['home', 'about', 'projects', 'contact'].map(item => (
                  <button
                    key={item}
                    onClick={() => scrollToSection(item)}
                    className={`menu-item capitalize text-sm font-medium transition-all duration-300 relative group ${activeSection === item ? 'text-cyan-400' : 'text-gray-300 hover:text-white'}`}
                  >
                    {item}
                    <span className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-cyan-400 to-blue-500 transition-all duration-300 ${activeSection === item ? 'w-full' : 'w-0 group-hover:w-full'}`}></span>
                  </button>
                ))}
              </div>
            </div>

            <div className="hidden md:flex space-x-6">
              <a href="https://github.com/Leaya0214" target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center text-gray-300 hover:text-cyan-400 transition-all duration-300 hover:scale-125 transform">
                <Github size={20} />
              </a>
              <a href="https://www.linkedin.com/in/leaya-sultana-74b1a21b4/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 flex items-center justify-center text-gray-300 hover:text-cyan-400 transition-all duration-300 hover:scale-125 transform">
                <Linkedin size={20} />
              </a>
            </div>

            <button className="md:hidden text-white" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {isMenuOpen && (
            <div className="md:hidden bg-black/90 backdrop-blur-md border-t border-gray-700 animate-in fade-in slide-in-from-top-2 duration-300">
              {['home', 'about', 'projects', 'contact'].map(item => (
                <button key={item} onClick={() => scrollToSection(item)} className="menu-item block w-full text-left px-6 py-4 capitalize text-sm text-gray-300 hover:text-cyan-400 hover:bg-white/5 transition-colors">
                  {item}
                </button>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Project Modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-gray-900/95 border border-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in scale-in duration-300 backdrop-blur">
            <div className="sticky top-0 bg-gray-900/95 border-b border-gray-800 p-6 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-white">{selectedProject.title}</h2>
              <button
                onClick={() => setSelectedProject(null)}
                className="w-10 h-10 flex items-center justify-center hover:bg-gray-800 rounded-lg transition-colors"
              >
                <XIcon size={24} className="text-gray-300" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <img src={selectedProject.image} alt={selectedProject.title} className="w-full h-64 object-cover rounded-xl" />

              <div>
                <span className="text-xs font-semibold text-cyan-400 uppercase tracking-widest bg-cyan-400/10 px-3 py-1 rounded-full">{selectedProject.category}</span>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-200 mb-2">Description</h3>
                <p className="text-gray-400 leading-relaxed">{selectedProject.desc}</p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-200 mb-2">Details</h3>
                <p className="text-gray-400 leading-relaxed">{selectedProject.details}</p>
              </div>

              <div className="pt-6 border-t border-gray-800">
                {selectedProject.link !== '#' && (
                  <a
                    href={selectedProject.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-400 to-blue-500 text-black rounded-lg hover:shadow-lg hover:shadow-cyan-400/50 transition-all duration-300 font-semibold hover:scale-105"
                  >
                    View on GitHub <ExternalLink size={18} />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Section opener overlay - REMOVED */}
      {/* এখানের pop up animation remove করা হয়েছে */}

      {/* Corner decorations */}
      <div className="corner-deco left">
        <div className="line"></div>
        <div className="box"></div>
      </div>
      <div className="corner-deco right">
        <div className="line"></div>
        <div className="box"></div>
      </div>

      {/* Preloader */}
      {isLoading && (
        <div className="preloader-overlay fixed inset-0 z-60 flex items-center justify-center bg-gradient-to-b from-black via-gray-950 to-black">
          <div className="text-center animate-fade-in">
            <div className="mb-6 animate-bounce" style={{ animationDuration: '1.5s' }}>
              <svg width="120" height="120" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="robot-svg robot-wave mx-auto">
                <rect x="10" y="20" width="60" height="40" rx="8" fill="#071028" stroke="#22d3ee" strokeWidth="1" />
                <rect x="24" y="10" width="32" height="16" rx="4" fill="#071028" stroke="#22d3ee" strokeWidth="1" />
                <circle cx="30" cy="36" r="3" fill="#22d3ee" />
                <circle cx="50" cy="36" r="3" fill="#22d3ee" />
                <g className="arm">
                  <rect x="62" y="28" width="8" height="3" rx="1" fill="#22d3ee" />
                </g>
              </svg>
            </div>
            <div className="text-cyan-400 font-medium text-lg">Loading <span className="wave">👋</span></div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center justify-center pt-20 px-4 relative overflow-hidden bg-black">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/30 rounded-full blur-3xl opacity-50 -mr-48 -mt-48 animate-blob"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl opacity-50 -ml-48 -mb-48 animate-blob animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-cyan-400/20 rounded-full blur-3xl opacity-30 -translate-x-1/2 -translate-y-1/2 animate-blob animation-delay-4000"></div>
        </div>

        <div className="max-w-4xl mx-auto text-center z-10">
          <div className="mb-8 animate-fade-in" style={{ animationDuration: '1s' }}>
            <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-8 border-2 border-cyan-400/50 shadow-lg shadow-cyan-400/30 hover:shadow-cyan-400/60 transition-all duration-500 hover:scale-110 transform">
              <img src="/me.jpeg" alt="Profile" className="w-50 h-50 object-cover" />
            </div>
          </div>

          <div className="mb-8">
            <p className="text-cyan-400 text-xs font-semibold uppercase tracking-[0.15em] mb-6 animate-slide-up" style={{ animationDelay: '0.1s' }}>Software Engineer</p>
            <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-tight animate-slide-up" style={{ animationDelay: '0.2s' }}>
              I'm <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent animate-gradient">Leaya</span>
            </h1>
          </div>

          <p className="text-lg md:text-xl text-gray-300 font-medium mb-4 animate-slide-up" style={{ animationDelay: '0.3s' }}>
            Software Engineer specializing in backend development.
          </p>

          <p className="text-sm md:text-base text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed font-light animate-slide-up" style={{ animationDelay: '0.4s' }}>
              Over 2 years of professional experience designing robust backend systems, architecting scalable enterprise solutions, and writing clean, efficient code.          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 animate-slide-up" style={{ animationDelay: '0.5s' }}>
            <button onClick={() => scrollToSection('projects')} className="px-8 py-3.5 bg-white text-black rounded-lg shadow-lg shadow-white/20 hover:shadow-2xl hover:shadow-white/30 transition-all duration-300 font-semibold text-sm flex items-center justify-center gap-2 hover:scale-110 transform group">
              View My Work <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => scrollToSection('contact')} className="px-8 py-3.5 border-2 border-white/30 text-white rounded-lg hover:border-cyan-400/50 hover:bg-cyan-400/5 transition-all duration-300 font-semibold text-sm hover:scale-105 transform">
              Get In Touch
            </button>
          </div>

          <div className="flex gap-4 justify-center md:hidden animate-slide-up" style={{ animationDelay: '0.6s' }}>
            <a href="https://github.com/Leaya0214" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center text-gray-300 hover:text-cyan-400 transition-all border border-gray-600 rounded-lg hover:border-cyan-400/50 hover:bg-cyan-400/5 hover:scale-125 transform">
              <Github size={20} />
            </a>
            <a href="https://www.linkedin.com/in/leaya-sultana-74b1a21b4/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 flex items-center justify-center text-gray-300 hover:text-cyan-400 transition-all border border-gray-600 rounded-lg hover:border-cyan-400/50 hover:bg-cyan-400/5 hover:scale-125 transform">
              <Linkedin size={20} />
            </a>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-cyan-400/50 rounded-full flex items-center justify-center">
            <div className="w-1 h-2 bg-cyan-400/50 rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 px-4 bg-gradient-to-b from-black to-gray-950 border-t border-gray-800 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl opacity-40 -mr-48 -mt-48"></div>

        <div className="container mx-auto max-w-5xl relative z-10">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-4 animate-fade-in">
            About <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Me</span>
          </h2>
          <p className="text-lg text-gray-400 mb-16 font-light animate-fade-in">Software Engineer (Backend Focused)</p>

          <div className="grid md:grid-cols-2 gap-16 mb-16">
            <div className="space-y-6 animate-slide-in-left">
              <p className="text-lg text-gray-300 font-light leading-relaxed">
                I’m a passionate Software Engineer focused on backend development, building robust backend solutions since 2022 that drive real business impact and serve thousands of users.
              </p>
              <p className="text-lg text-gray-300 font-light leading-relaxed">
                My expertise spans across complex domains — from enterprise resource planning to e-commerce platforms. I thrive on solving intricate technical problems and optimizing system performance for scale.
              </p>
              <p className="text-lg text-gray-300 font-light leading-relaxed">
                Clean code, architectural patterns, and continuous learning are at the core of my development philosophy. I'm always exploring new technologies and best practices to deliver better solutions.
              </p>
            </div>

            <div className="space-y-8 animate-slide-in-right">
              <div className="bg-gradient-to-r from-cyan-400/10 to-blue-500/10 border border-cyan-400/20 rounded-lg p-8 hover:border-cyan-400/50 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-cyan-400/20">
                <h3 className="text-sm font-semibold text-cyan-400 uppercase tracking-wider mb-3">Currently</h3>
                <p className="text-2xl text-white font-semibold mb-1">Software Engineer at STITBD</p>
                <p className="text-sm text-gray-400">August 2023 - Present</p>
              </div>

              <div className="bg-gradient-to-r from-cyan-400/10 to-blue-500/10 border border-cyan-400/20 rounded-lg p-8 hover:border-cyan-400/50 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-cyan-400/20">
                <h3 className="text-sm font-semibold text-cyan-400 uppercase tracking-wider mb-3">Education</h3>
                <p className="text-2xl text-white font-semibold mb-1">BSc in Computer Science</p>
                <p className="text-sm text-gray-400">National University, 2022</p>
              </div>

              <div className="bg-gradient-to-r from-cyan-400/10 to-blue-500/10 border border-cyan-400/20 rounded-lg p-8 hover:border-cyan-400/50 transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-cyan-400/20">
                <h3 className="text-sm font-semibold text-cyan-400 uppercase tracking-wider mb-3">Experience</h3>
                <p className="text-2xl text-white font-semibold mb-1">2+ Years Professional</p>
                <p className="text-sm text-gray-400">Backend Development & Architecture</p>
              </div>

              <button className="w-full px-6 py-4 bg-gradient-to-r from-cyan-400 to-blue-500 text-black rounded-lg hover:shadow-2xl hover:shadow-cyan-400/50 transition-all duration-300 font-semibold flex items-center justify-center gap-2 hover:scale-105 transform">
                <Download size={20} />
                Download CV
              </button>
            </div>
          </div>

          <div className="pt-16 border-t border-gray-800">
            <h3 className="text-3xl font-bold text-white mb-12 animate-fade-in">Technical <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Expertise</span></h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { skill: 'Laravel', level: '95%' },
                { skill: 'PHP', level: '90%' },
                { skill: 'MySQL', level: '80%' },
                { skill: 'HTML/CSS', level: '95%' },
                { skill: 'JavaScript', level: '70%' },
                { skill: 'CodeIgniter', level: '70%' },
                { skill: 'Git/GitHub', level: '60%' },
                { skill: 'API Design', level: '85%' }
              ].map((item, idx) => (
                <div key={idx} className="bg-gray-900/50 border border-gray-800 hover:border-cyan-400/30 p-5 rounded-lg transition-all duration-300 group hover:bg-gray-800/50 transform hover:scale-105 hover:shadow-lg hover:shadow-cyan-400/10">
                  <p className="text-sm text-gray-300 font-medium mb-3 group-hover:text-cyan-400 transition-colors">{item.skill}</p>
                  <div className="w-full bg-gray-700 rounded-full h-1.5 overflow-hidden">
                    <div className="bg-gradient-to-r from-cyan-400 to-blue-500 h-1.5 rounded-full transition-all duration-1000" style={{ width: item.level }}></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-3">{item.level}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-32 px-4 bg-black border-t border-gray-800">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-4 animate-fade-in">
            Featured <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Projects</span>
          </h2>
          <p className="text-lg text-gray-400 font-light mb-16 animate-fade-in">Transforming ideas into scalable backend solutions</p>

          <div className="grid md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="group bg-gray-900/50 border border-gray-800 rounded-xl overflow-hidden hover:border-cyan-400/50 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-400/10 transform hover:scale-105 cursor-pointer animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="relative overflow-hidden h-64">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover group-hover:scale-125 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                <div className="p-8">
                  <span className="text-xs font-semibold text-cyan-400 uppercase tracking-widest bg-cyan-400/10 px-3 py-1 rounded-full inline-block mb-4">{project.category}</span>
                  <h3 className="text-2xl font-bold text-white mt-2 mb-3 group-hover:text-cyan-400 transition-colors">{project.title}</h3>
                  <p className="text-sm text-gray-400 font-light leading-relaxed mb-4">{project.desc}</p>
                  <button
                    onClick={() => setSelectedProject(project)}
                    className="inline-flex items-center text-cyan-400 text-sm font-medium group-hover:translate-x-2 transition-transform hover:text-cyan-300"
                  >
                    Learn more <ArrowRight size={16} className="ml-2" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 px-4 bg-gradient-to-b from-black to-gray-950 border-t border-gray-800 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl opacity-40 -ml-48 -mb-48"></div>

        <div className="container mx-auto max-w-5xl relative z-10">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-4 text-center animate-fade-in">
            Let's <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">Connect</span>
          </h2>
          <p className="text-lg text-gray-400 font-light text-center mb-16 animate-fade-in">Have an exciting project or just want to discuss ideas?</p>

          <div className="grid md:grid-cols-3 gap-6 mb-16 animate-slide-up">
            <a href="mailto:leayasultana@gmail.com" className="group bg-gray-900/50 border border-gray-800 p-8 rounded-lg hover:border-cyan-400/50 hover:bg-gray-900 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-400/10 transform hover:scale-105 hover:-translate-y-2">
              <div className="w-12 h-12 bg-gradient-to-r from-cyan-400/20 to-blue-500/20 rounded-lg flex items-center justify-center mb-6 group-hover:from-cyan-400/40 group-hover:to-blue-500/40 transition-all group-hover:scale-110 transform">
                <Mail className="text-cyan-400" size={24} />
              </div>
              <h3 className="font-semibold text-white mb-2 text-lg">Email</h3>
              <p className="text-sm text-gray-400 font-light truncate group-hover:text-gray-300 transition-colors">leayasultana@gmail.com</p>
            </a>

            <a href="tel:+8801732369984" className="group bg-gray-900/50 border border-gray-800 p-8 rounded-lg hover:border-cyan-400/50 hover:bg-gray-900 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-400/10 transform hover:scale-105 hover:-translate-y-2">
              <div className="w-12 h-12 bg-gradient-to-r from-cyan-400/20 to-blue-500/20 rounded-lg flex items-center justify-center mb-6 group-hover:from-cyan-400/40 group-hover:to-blue-500/40 transition-all group-hover:scale-110 transform">
                <Phone className="text-cyan-400" size={24} />
              </div>
              <h3 className="font-semibold text-white mb-2 text-lg">Phone</h3>
              <p className="text-sm text-gray-400 font-light group-hover:text-gray-300 transition-colors">+880 1732 369984</p>
            </a>

            <div className="bg-gray-900/50 border border-gray-800 p-8 rounded-lg transform hover:scale-105 transition-all duration-300">
              <div className="w-12 h-12 bg-gradient-to-r from-cyan-400/20 to-blue-500/20 rounded-lg flex items-center justify-center mb-6">
                <MapPin className="text-cyan-400" size={24} />
              </div>
              <h3 className="font-semibold text-white mb-2 text-lg">Location</h3>
              <p className="text-sm text-gray-400 font-light">Dhaka, Bangladesh</p>
            </div>
          </div>

          <div className="bg-gray-900/50 border border-gray-800 p-12 rounded-xl backdrop-blur animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <h3 className="text-2xl font-bold text-white mb-8">Send me a message</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <input
                  type="text"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-4 bg-gray-800/50 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:border-cyan-400 text-white placeholder-gray-500 font-light transition-all duration-300 focus:scale-105"
                  required
                />
                <input
                  type="email"
                  placeholder="Your Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-4 bg-gray-800/50 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:border-cyan-400 text-white placeholder-gray-500 font-light transition-all duration-300 focus:scale-105"
                  required
                />
              </div>
              <textarea
                rows={5}
                placeholder="Your Message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-4 bg-gray-800/50 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:border-cyan-400 text-white placeholder-gray-500 font-light transition-all duration-300 resize-none focus:scale-105"
                required
              ></textarea>
              <button type="submit" className="w-full bg-gradient-to-r from-cyan-400 to-blue-500 text-black py-4 rounded-lg hover:shadow-2xl hover:shadow-cyan-400/50 transition-all duration-300 font-semibold hover:scale-105 transform">
                Send Message
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Robot helper */}
      <div
        ref={robotRef}
        className={`site-robot fixed z-50 opacity-95 ${isLoading ? 'robot-center-load robot-wave' : robotPos === 'hero-top-left' ? 'robot-hero-left' : 'robot-bottom-right'}`}
      >
        <img src="/robot.png" alt="robot" className="robot-img" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        <svg width="84" height="84" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="robot-svg">
          <rect x="8" y="20" width="64" height="40" rx="8" fill="#071028" stroke="#06b6d4" strokeWidth="1" />
          <rect x="22" y="10" width="36" height="16" rx="4" fill="#071028" stroke="#06b6d4" strokeWidth="1" />
          <circle cx="32" cy="36" r="3" fill="#22d3ee" />
          <circle cx="48" cy="36" r="3" fill="#22d3ee" />
          <g className="arm">
            <rect x="58" y="30" width="10" height="4" rx="2" fill="#22d3ee" />
          </g>
        </svg>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-16 px-4 bg-black">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div>
              <p className="text-2xl font-bold text-white tracking-tight">leaya<span className="text-cyan-400">.</span></p>
              <p className="text-gray-400 font-light text-sm mt-2">Software Engineer</p>
            </div>
            <div className="flex gap-8">
              <a href="https://github.com/Leaya0214" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-cyan-400 transition-all duration-300 hover:scale-150 transform">
                <Github size={24} />
              </a>
              <a href="https://www.linkedin.com/in/leaya-sultana-74b1a21b4/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-cyan-400 transition-all duration-300 hover:scale-150 transform">
                <Linkedin size={24} />
              </a>
            </div>
            <p className="text-gray-400 font-light text-sm">© 2025 Leaya. Crafted with precision</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Portfolio;